"use client";

import { cn } from "@/components/libs/cn";
import { useIsDesktop } from "@/shared/useIsDesktop";
import { useIsRendered } from "@/shared/useIsRendered";
import { isE2e } from "@/shared/utils/isE2e";
import { useCallback, useMemo } from "react";

// Simple wave parameters
const WAVE_HEIGHT = 150;
const WAVE_BASELINE = 0.5; // Where the wave sits (0.3 = low, 0.7 = high)

const DEFAULT_COLORS = {
  top: "#E2DBFD",
  bottom: "#FEDEE1",
} as const;

interface WavyDividerProps {
  readonly className?: string;
  readonly topColor?: string;
  readonly bottomColor?: string;
  readonly flip?: boolean;
  readonly animated?: boolean;
  readonly direction?: "left" | "right";
  readonly id?: number;
}

export function WavyDivider({
  className,
  topColor = DEFAULT_COLORS.top,
  bottomColor = DEFAULT_COLORS.bottom,
  flip = false,
  animated = true,
  direction = "left",
  id = 0,
}: WavyDividerProps) {
  const isDesktop = useIsDesktop();
  const isRendered = useIsRendered();
  const isE2eEnvironment = useMemo(() => isE2e(), []);

  const { maskId, gradientId } = useMemo(
    () => ({
      maskId: `wave-mask-${id}`,
      gradientId: `gradient-${id}`,
    }),
    [id],
  );

  // Calculate wave parameters
  const WAVE_AMPLITUDE = isDesktop ? 0.5 : 0.2; // How pronounced the waves are (0.1 = subtle, 0.3 = dramatic)
  const waveBaseY = WAVE_HEIGHT * WAVE_BASELINE;
  const waveAmplitude = WAVE_HEIGHT * WAVE_AMPLITUDE;
  const waveFrequency = 600;
  const viewBoxWidth = 1200;

  // Extended wave path - much longer than viewBox for seamless infinite animation
  const wavePath = `
    M -${waveFrequency} ${WAVE_HEIGHT}
    L -${waveFrequency} ${waveBaseY}
    Q ${waveFrequency * -0.75} ${waveBaseY - waveAmplitude} ${waveFrequency * -0.5} ${waveBaseY}
    T ${waveFrequency * 0} ${waveBaseY}
    T ${waveFrequency * 0.5} ${waveBaseY}
    T ${waveFrequency * 1} ${waveBaseY}
    T ${waveFrequency * 1.5} ${waveBaseY}
    T ${waveFrequency * 2} ${waveBaseY}
    T ${waveFrequency * 2.5} ${waveBaseY}
    T ${waveFrequency * 3} ${waveBaseY}
    T ${waveFrequency * 3.5} ${waveBaseY}
    T ${waveFrequency * 4} ${waveBaseY}
    T ${waveFrequency * 4.5} ${waveBaseY}
    T ${waveFrequency * 5} ${waveBaseY}
    L ${waveFrequency * 5} ${WAVE_HEIGHT}
    Z
  `;

  const shouldAnimate = useCallback(() => {
    if (!animated || !isRendered) return false;
    if (isE2eEnvironment) return false;
    const prefersReducedMotion =
      typeof globalThis.matchMedia === "function"
        ? globalThis.matchMedia("(prefers-reduced-motion: reduce)").matches
        : false;
    return !prefersReducedMotion;
  }, [animated, isE2eEnvironment, isRendered]);

  const animationDistance = waveFrequency;
  const animationDuration = isDesktop ? "8s" : "6s";

  // Flip colors when flipped
  const gradientTopColor = flip ? bottomColor : topColor;
  const gradientBottomColor = flip ? topColor : bottomColor;

  return (
    <div className={cn("relative w-full overflow-hidden", className)}>
      <div
        className="relative"
        style={{
          height: `${WAVE_HEIGHT}px`,
          transform: flip ? "scaleY(-1)" : "scaleY(1)",
        }}
      >
        <svg
          className="absolute inset-0 size-full"
          viewBox={`0 0 ${viewBoxWidth} ${WAVE_HEIGHT}`}
          preserveAspectRatio="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <mask id={maskId}>
              <rect width="100%" height="100%" fill="white" />
              <g fill="black">
                <path d={wavePath}>
                  {shouldAnimate() && (
                    <animateTransform
                      attributeName="transform"
                      type="translate"
                      values={
                        direction === "left"
                          ? `0 0; ${-animationDistance} 0`
                          : `0 0; ${animationDistance} 0`
                      }
                      dur={animationDuration}
                      repeatCount="indefinite"
                    />
                  )}
                </path>
              </g>
            </mask>

            <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor={gradientTopColor} />
              <stop offset="100%" stopColor={gradientBottomColor} />
            </linearGradient>
          </defs>

          <rect
            width="100%"
            height="100%"
            fill={`url(#${gradientId})`}
            mask={`url(#${maskId})`}
          />
        </svg>
      </div>
    </div>
  );
}
